import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Mutation, MutationUpdateAccessGrantArgs, Query, UpdateAccessGrantInput } from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT } from 'graphql/v2/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES } from 'graphql/v2/queries';
import { filterGroupsForUser } from 'hooks/shared/filterGroupsForUser';
import { useEffect } from 'react';
import { authVar } from 'state/vars';
import { TranslatedHookProps } from '../hooks.types';

export type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const useSchedules = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const { user } = useReactiveVar(authVar);

  const { data, loading, error } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(
    GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES
  );

  useEffect(() => {
    if (error) handleFetchError('Error while fetching schedules');
  }, [error]);

  const schedules = filterGroupsForUser(data?.getVisitorGroupsForTenant ?? [], user);

  const [updateAccessGrantMutation, { loading: updateAccessGrantMutationLoading }] = useMutation<
    Pick<Mutation, 'updateAccessGrant'>,
    MutationUpdateAccessGrantArgs
  >(UPDATE_ACCESS_GRANT, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.error_while_updating_schedule)
  });

  const updateAccessGrant = async (
    accessGrantId: string,
    input: UpdateAccessGrantInput
  ): Promise<{ success: boolean }> => {
    const { data: mutationData } = await updateAccessGrantMutation({
      variables: { accessGrantId, input }
    });
    if (!mutationData?.updateAccessGrant?.id) {
      handleFetchError(translation.error_while_updating_schedule);
      return { success: false };
    }
    return { success: true };
  };

  return {
    schedules,
    updateAccessGrant,
    updateAccessGrantMutationLoading,
    loading
  };
};

export default useSchedules;
